import { ExclamationCircleOutlined } from "@ant-design/icons"
import { Vendor } from "@finway-group/shared/lib/models"
import * as Sentry from "@sentry/react"
import { Button, Checkbox, Form, Modal, Tooltip } from "antd"
import { type ButtonProps } from "antd/lib/button"
import { type Store } from "antd/lib/form/interface"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useHistory, useParams } from "react-router-dom"
import { FormControlsArrowKeysFix } from "~Components/form/formControlsArrowKeysFix"
import { VendorForm } from "~Components/forms/vendor.form"
import { useAuth } from "~Shared/hooks/auth.hooks"
import { useFormTouchedHandler } from "~Shared/hooks/form.hooks"
import { defaultQueryParamsObject } from "~Shared/queries/vendors/consts"
import { useCreateVendor, useRestoreVendor, useUpdateVendor } from "~Shared/queries/vendors/vendor.mutations"
import { useVendor } from "~Shared/queries/vendors/vendor.queries"
import { CompanyScopedRoutePathEnum } from "~Shared/router/RoutePath.enum"
import { NotificationService, VendorService } from "~Shared/services"
import { DialogService } from "~Shared/services/dialog.service"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import { type ErrorInterface } from "~Shared/utils/errorHandler.utils"
import { copyDeeply, getTooltipPopupContainer, removeStringWhiteSpaces, shouldRestoreItem, validateCreditorNumberDATEVCompatibility } from "~Shared/utils/helper.utils"
import { useStateIfMounted } from "~Shared/utils/hooks/useStateIfMounted"
import { DragModalTypeEnum, dragElement } from "~Shared/utils/plugins/draftjs/draggable"
import { generatePath } from "~Shared/utils/routing.utils"

const { confirm } = Modal

interface VendorFormModalProps {
    isShowing: boolean
    isNew: boolean
    isOpenedByExpenseModal?: boolean
    prefilledData?: Partial<Vendor>
    onCancel: (state: boolean, vendor?: Vendor) => void
    shouldRedirectOnRestoration?: boolean
    isDraggable?: boolean
}
export const VendorFormModal = ({
    isShowing,
    isNew,
    prefilledData,
    isOpenedByExpenseModal = false,
    onCancel,
    shouldRedirectOnRestoration = true,
    isDraggable = false,
}: VendorFormModalProps) => {
    const [formInstance] = Form.useForm()
    const [isLoading, setIsLoading] = useStateIfMounted<boolean>(false)
    const [isUploading, setIsUploading] = useStateIfMounted(false)
    const [shouldSyncExpenses, setShouldSyncExpenses] = useStateIfMounted(false)
    const { t } = useTranslation()
    const history = useHistory()
    const { userClass } = useAuth()
    const { id } = useParams<{ id: string }>()
    const { data: vendor } = useVendor(id)
    const createVendorMutation = useCreateVendor()
    const updateVendorMutation = useUpdateVendor()
    const restoreVendorMutation = useRestoreVendor()
    const { isFormTouched, handleTouchForm, handleResetTouchForm } = useFormTouchedHandler()

    useEffect(() => {
        if (!isNew && isShowing) {
            formInstance.setFieldsValue(vendor)
        } else if (prefilledData) {
            formInstance.setFieldsValue(prefilledData)
        }

        if (isShowing && isDraggable) {
            const modalElement = document.getElementsByClassName("draggableVendorModal")[0] as HTMLElement
            dragElement(modalElement, "dragVendorModalHeader", DragModalTypeEnum.small)
        }

        return () => {
            if (isShowing) {
                formInstance.resetFields()
            }
        }
    }, [isShowing])

    const onSubmit = async (values: Store, forceSubmit?: boolean) => {
        // extra variable to change it after confirmation dialog
        let shouldSynchronizeExpenses = shouldSyncExpenses

        if (!forceSubmit) {
            try {
                validateCreditorNumberDATEVCompatibility(values.creditorNumber)
            } catch (err) {
                confirm({
                    title: t("confirm:vendor.incompatible_creditor_number.title"),
                    content: t(`confirm:vendor.incompatible_creditor_number.message`, { reason: err.message }),
                    cancelText: t("confirm:vendor.incompatible_creditor_number.cancel"),
                    type: "warning",
                    okText: t("confirm:vendor.incompatible_creditor_number.confirm"),
                    onOk() {
                        return onSubmit(values, true)
                    },
                })
                return
            }
        }

        if (!shouldSynchronizeExpenses && !isNew && (await DialogService.confirmSync())) {
            shouldSynchronizeExpenses = true
        }

        try {
            const img = values.img?.file ? values.img.file.xhr : values.img
            const potentialDuplicateVendors = await VendorService.checkForDuplicateVendors({ ...values, img })
            const shouldShowDuplicateConfirmation = isNew && potentialDuplicateVendors.length > 0
            if (shouldShowDuplicateConfirmation) {
                const { resolved, chosenVendor } = await DialogService.confirmCreationOfPotentialDuplicateVendor(
                    { ...values, img } as Vendor,
                    potentialDuplicateVendors,
                    isOpenedByExpenseModal,
                )
                if (!resolved) {
                    return
                }
                if (chosenVendor) {
                    handleHide(chosenVendor)
                    return
                }
            }
        } catch (err) {
            Sentry.captureException(`[VendorModal] Check for duplicate vendor failed: ${typeof err === "string" ? err : JSON.stringify(err)}`)
        }

        setIsLoading(true)

        const vendorData = buildVendorUpdateData(values)

        if (vendorData.iban) {
            vendorData.iban = removeStringWhiteSpaces(vendorData.iban)
        }
        if (isNew) {
            await handleNewVendorCreation(vendorData)
        } else {
            await handleVendorUpdate(vendorData, shouldSynchronizeExpenses)
        }
    }

    // TODO: refactor like in employee modal
    const buildVendorUpdateData = (values: Store) =>
        new Vendor(
            !isNew
                ? {
                      ...vendor,
                      ...values,
                      img: vendor?.img !== values.img ? (values.img.file ? values.img.file.xhr : values.img) : vendor?.img,
                  }
                : {
                      ...values,
                      createdBy: userClass?.id,
                      img: values.img && values.img?.file ? values.img.file.xhr : values.img,
                  },
        )

    const handleNewVendorCreation = async (vendorData: Vendor) => {
        try {
            const createdVendor = await createVendorMutation.mutateAsync(vendorData)
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:vendor.created.title"), t("notification:vendor.created.message"))
            handleHide(createdVendor)
        } catch (error) {
            await handleNewVendorCreationError(error, vendorData)
        }
    }

    const handleNewVendorCreationError = async (error: ErrorInterface | any, vendorUpdateData: Vendor) => {
        setIsLoading(false)
        const vendorsConflictingByName = (
            await VendorService.fetchAllVendors({
                ...defaultQueryParamsObject,
                limit: 1,
                name: vendorUpdateData.name,
            })
        ).docs
        const vendorsConflictingByCreditorNumber = (
            await VendorService.fetchAllVendors({
                ...defaultQueryParamsObject,
                limit: 1,
                creditorNumber: String(vendorUpdateData.creditorNumber),
            })
        ).docs

        const conflictVendor = [...vendorsConflictingByName, ...vendorsConflictingByCreditorNumber][0]

        if (shouldRestoreItem(error, conflictVendor)) {
            restoreVendor(copyDeeply(conflictVendor!, Vendor), vendorUpdateData)
        } else {
            NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:vendor.create.title"))
        }
    }

    const handleVendorUpdate = async (vendorData: Vendor, shouldSynchronizeExpenses: boolean) => {
        try {
            if (!id) {
                return
            }
            await updateVendorMutation.mutateAsync({ vendorId: id, updateVendorData: vendorData, shouldSynchronizeExpenses })

            handleHide()
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:vendor.updated.title"), t("notification:vendor.updated.message"))
        } catch (error) {
            setIsLoading(false)
            NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:vendor.edit.title"))
        }
    }

    const handleHide = (vendor?: Vendor) => {
        setIsLoading(false)
        formInstance.resetFields()
        onCancel(false, vendor)
    }

    const restoreVendor = (vendor: Vendor, conflictingData: any) => {
        const conflictingField = vendor.name === conflictingData.name ? "name" : "creditor_number"
        confirm({
            title: t("confirm:vendor.restore.title"),
            content: t(`confirm:vendor.restore.${conflictingField}_message`),
            cancelText: t("confirm:vendor.restore.cancel"),
            type: "warning",
            okText: t("confirm:vendor.restore.confirm"),
            onOk() {
                return handleRestore(vendor)
            },
            okButtonProps: { "data-testid": "vendorRestoreOkButton" } as ButtonProps,
        })
    }

    const handleRestore = async (vendorToRestore: Vendor) => {
        try {
            const restoredVendor = await restoreVendorMutation.mutateAsync({ vendorId: vendorToRestore.id, updateVendorData: vendorToRestore })
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:vendor.restored.title"), t("notification:vendor.restored.message"))
            handleHide(restoredVendor)
            if (shouldRedirectOnRestoration) {
                history.push(generatePath(CompanyScopedRoutePathEnum.VENDOR_DETAIL, { id: vendorToRestore.id }))
            }
        } catch (err) {
            setIsLoading(false)
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:cost_center.edit.title"))
        }
    }

    const resetLoadingAndForm = () => {
        formInstance.resetFields()
        setIsLoading(false)
        handleResetTouchForm()
        onCancel(false)
    }

    const handleHideOnCancel = async () => {
        if (isFormTouched.current) {
            const isConfirmed = await DialogService.confirmUnsavedChanges()
            if (!isConfirmed) {
                return
            }
        }

        resetLoadingAndForm()
    }

    return (
        <FormControlsArrowKeysFix>
            <Modal
                destroyOnClose={true}
                afterClose={() => formInstance.resetFields()}
                open={isShowing}
                maskClosable={false}
                title={<span id="dragVendorModalHeader">{isNew ? t("action:vendor.create") : t("action:vendor.edit")}</span>}
                onCancel={handleHideOnCancel}
                closable={true}
                keyboard={true}
                className={`ant-modal--small draggableVendorModal ${isDraggable ? "absolute" : ""}`}
                footer={
                    <div className="flex justify-between items-center">
                        {!isNew && (
                            <div>
                                <Checkbox className="ml-8" onChange={(e) => setShouldSyncExpenses(e.target.checked)}>
                                    {t("input:sync_updates")}
                                </Checkbox>
                                <Tooltip getPopupContainer={getTooltipPopupContainer} title={t("tooltips:synch_expenses:vendor")} placement="top" className="align-middle">
                                    <ExclamationCircleOutlined />
                                </Tooltip>
                            </div>
                        )}
                        <div className="ml-auto mr-0">
                            <Button data-testid="finway:vendor:modal:cancel-btn" key="back" onClick={handleHideOnCancel}>
                                {t("action:cancel")}
                            </Button>

                            <Button
                                data-testid="finway:vendor:modal:submit-btn"
                                key="submit"
                                type="primary"
                                loading={isLoading}
                                disabled={isUploading}
                                onClick={() => {
                                    formInstance.submit()
                                }}
                            >
                                {isNew ? t("action:vendor.submit") : t("action:vendor.save")}
                            </Button>
                        </div>
                    </div>
                }
            >
                <VendorForm
                    isNew={isNew}
                    vendor={vendor}
                    prefilledData={prefilledData}
                    formInstance={formInstance}
                    onSubmit={onSubmit}
                    setIsUploading={setIsUploading}
                    onFormValuesChange={handleTouchForm}
                />
            </Modal>
        </FormControlsArrowKeysFix>
    )
}

// TODO get rid of default export
